import { QueryClient } from "@tanstack/react-query";
import { Polygon } from "geojson";
import { z } from "zod";

import { getWmsRequest } from "@nens/react-map-gl/utils";
import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { BoundingBox, combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { isoDurationSchema } from "../isoDuration";
import { paginatedSchema } from "../pagination";

import { layerCollectionSchema } from "./layercollections";
import { observationTypeSchema } from "./observationtypes";
import { organisationSchema } from "./organisations";

export const rastersListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  last_modified: lizardDateSchema.nullable(),
  created: lizardDateSchema,
  organisation: organisationSchema,
  access_modifier: z.string(),
  supplier: z.string().nullable(),
  shared_with: z.array(organisationSchema),
  writable: z.boolean(),
  is_geoblock: z.boolean(),
  weight: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  observation_type: observationTypeSchema.nullable(),
  temporal: z.boolean(),
  layer_collections: z.array(layerCollectionSchema),
  first_value_timestamp: lizardDateSchema.nullable(),
  last_value_timestamp: lizardDateSchema.nullable(),
  spatial_bounds: z
    .object({
      west: z.number(),
      east: z.number(),
      north: z.number(),
      south: z.number(),
    })
    .nullable(),
  projection: z.string().nullable(),
  origin_x: z.number().nullable(),
  origin_y: z.number().nullable(),
  upper_bound_x: z.number().nullable(),
  upper_bound_y: z.number().nullable(),
  pixelsize_x: z.number().nullable(),
  pixelsize_y: z.number().nullable(),
  interval: isoDurationSchema.nullable(),
  wms_info: z.object({
    endpoint: z.string(),
    layer: z.string(),
  }),
  options: z.record(z.any()) /* So far seen: strings, numbers, booleans, objects... */,
  rescalable: z.boolean(),
  aggregation_type: z.string() /* Enum? */,
});

export type RasterListItem = z.infer<typeof rastersListItemSchema>;

export const rasterListSchema = paginatedSchema(rastersListItemSchema);
export type RasterList = z.infer<typeof rasterListSchema>;

/* The rasters detail has a few more fields than the list version! */
export const rasterSchema = rastersListItemSchema.extend({
  source: z.object({}).nullable() /* TODO */,
  raster_sources: z.array(z.string()) /* TODO */,
  colormap: z.union([
    z.object({}),
    z.object({
      type: z.string(),
      data: z.array(z.tuple([z.number(), z.array(z.number())])),
      free: z.boolean().optional(),
    }),
  ]),
});

export type Raster = z.infer<typeof rasterSchema>;

export function useRasters(params = {}) {
  const url = combineUrlAndParams("/api/v4/rasters/", params);
  return useParsedQuery<RasterList>(
    ["lizard-api-v4", "rasters", url],
    () => fetchWithError(url),
    (json: unknown) => rasterListSchema.parse(json)
  );
}

export function useRaster(uuid: string) {
  return useParsedQuery<Raster>(
    ["lizard-api-v4", "rasters", uuid],
    () => fetchWithError(`/api/v4/rasters/${uuid}/`),
    (json: unknown) => rasterSchema.parse(json),
    { enabled: uuid !== "" }
  );
}

// Use this outside components, so the fetch raster can still
// be stored in cache.
export async function fetchAndCacheRaster(
  queryClient: QueryClient,
  uuid: string
): Promise<Raster | null> {
  try {
    const response = await fetchWithError(`/api/v4/rasters/${uuid}/`);
    const raster = rasterSchema.parse(response);

    queryClient.setQueryData(["lizard-api-v4", "rasters", uuid], raster);
    return raster;
  } catch (e) {
    return null;
  }
}

export function useMultipleRasters(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "rasters", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/rasters/${uuid}/`),
        parseFunction: (json: unknown) => rasterSchema.parse(json),
      };
    })
  );
}

// Helper functions
export function getRasterWmsUrl(raster: Raster, timestamp?: string) {
  const { wms_info, options, temporal } = raster;
  let styles = options["styles"];
  let layer = wms_info.layer;

  if (styles && typeof styles !== "string") styles = styles["0"]["0"];

  if (wms_info.layer === "rain") layer = "radar:5min";

  const wmsUrl = getWmsRequest("/wms/", {
    layer: layer,
    styles: styles ?? "",
    transparent: false,
    ...(temporal && timestamp && { time: timestamp }),
  });

  return wmsUrl;
}

export function getRasterGeometry(raster: Raster): Polygon | null {
  const { spatial_bounds } = raster;

  if (!spatial_bounds) return null;

  const boundingBox = new BoundingBox(
    spatial_bounds.west,
    spatial_bounds.south,
    spatial_bounds.east,
    spatial_bounds.north
  );

  const { geometry } = boundingBox.toGeometry();
  return geometry;
}
